'use client'

import classes from './account-form.module.css'

import { useState } from 'react'

import { Button, Divider, Stack, TextInput, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver } from 'mantine-form-zod-resolver'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { z } from 'zod'

import { BASE_URL } from '@/constants/env'
import { useGetAccountBySubdomain } from '@/lib/api/account/account.mutations'

import AccountHelpfulInfo from '../components/account-helpful-info'
import FormErrorMessage from '../components/form-error-message'
import RightSectionHelper from './components/right-section-helper'

type AccountFormData = {
  accountAlias: string
}

const AccountForm = () => {
  const t = useTranslations('auth')
  const router = useRouter()
  const accountMutation = useGetAccountBySubdomain()
  const [accountErrorMessage, setAccountErrorMessage] = useState<string | null>(null)

  const schema = z.object({
    accountAlias: z.string().min(1, {
      error: t('select-account.form.fields.account.error.required'),
    }),
  })

  const form = useForm<AccountFormData>({
    mode: 'uncontrolled',
    initialValues: {
      accountAlias: '',
    },
    validate: zod4Resolver(schema),
  })

  const handleSubmit = async (values: typeof form.values) => {
    setAccountErrorMessage(null)
    try {
      const account = await accountMutation.mutateAsync(values.accountAlias)
      if (account.slug) {
        router.push(`https://${account.slug}.${BASE_URL}`)
      }
    } catch (error) {
      console.error(error)
      setAccountErrorMessage(t('select-account.form.fields.account.error.not-found'))
    }
  }

  return (
    <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap='2xl'>
        <Stack gap='xl'>
          <Title size='h2' fz='xl' ta='center'>
            {t('select-account.title')}
          </Title>
          <Stack gap='lg'>
            <TextInput
              classNames={{ section: classes.section }}
              label={t('select-account.form.fields.account.label')}
              name='accountAlias'
              opacity={accountMutation.isPending ? 0.2 : 1}
              placeholder={t('select-account.form.fields.account.placeholder')}
              required
              rightSection={<RightSectionHelper />}
              size='xs'
              style={{
                transition: 'opacity 0.5s ease-out',
              }}
              {...form.getInputProps('accountAlias')}
            />
            <Button
              autoContrast
              loading={accountMutation.isPending}
              loaderProps={{ type: 'dots' }}
              type='submit'
              size='xs'
            >
              {t('select-account.form.submit.label')}
            </Button>
          </Stack>
          <FormErrorMessage message={accountErrorMessage} />
        </Stack>
        <Divider color='var(--mantine-color-border-surface)' />
        <AccountHelpfulInfo
          accountAlias={t('select-account.card-help.account-alias')}
          title={t('select-account.card-help.title')}
          description={t('select-account.card-help.description')}
        />
      </Stack>
    </form>
  )
}

export default AccountForm
